import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Button, Input, Modal, Select, Space, Table, Tag, message } from 'antd';
import { ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import { useLanguage } from '../contexts/LanguageContext';
import { commerceServerTablePagination, COMMERCE_DEFAULT_PAGE_SIZE } from '../constants/commerceListPagination';
import { parseCustomerNameLocalesRoot, resolveCustomerDisplayName } from '../constants/commerceNameLocales';

const CUSTOMER_STATUS_VALUES = ['Active', 'Suspended', 'Pending'];

const pickCommerceRow = (obj, ...keys) => {
  for (const key of keys) {
    if (obj?.[key] !== undefined && obj?.[key] !== null) return obj[key];
  }
  return null;
};

const normalizeCommerceCustomerRowForPicker = (row) => {
  if (!row || typeof row !== 'object') return row;
  return {
    ...row,
    id: pickCommerceRow(row, 'id', 'Id'),
    customer_no: pickCommerceRow(row, 'customer_no', 'customerNo', 'CustomerNo'),
    branch_no: pickCommerceRow(row, 'branch_no', 'branchNo', 'BranchNo'),
    name_1: pickCommerceRow(row, 'name_1', 'name1', 'Name1', 'name_en_1', 'nameEn1', 'NameEn1'),
    name_2: pickCommerceRow(row, 'name_2', 'name2', 'Name2', 'name_en_2', 'nameEn2', 'NameEn2'),
    name_3: pickCommerceRow(row, 'name_3', 'name3', 'Name3', 'name_en_3', 'nameEn3', 'NameEn3'),
    name_locales: pickCommerceRow(row, 'name_locales', 'nameLocales', 'NameLocales'),
    contact_name: pickCommerceRow(row, 'contact_name', 'contactName', 'ContactName'),
    tel: pickCommerceRow(row, 'tel', 'Tel'),
    customer_status: (() => {
      const s = pickCommerceRow(row, 'customer_status', 'customerStatus', 'CustomerStatus');
      if (s == null || s === '') return 'Active';
      const x = String(s).trim();
      return CUSTOMER_STATUS_VALUES.includes(x) ? x : 'Active';
    })()
  };
};

async function extractResponseError(response) {
  const text = await response.text();
  if (!text) return `HTTP ${response.status}`;
  try {
    const data = JSON.parse(text);
    return data?.error || data?.detail || data?.message || text;
  } catch {
    return text;
  }
}

/**
 * 與客戶主檔列表相同的表格選擇器：搜尋、租戶管理員可依公司篩選、多選或單選。
 */
const CommerceCustomerTablePickerModal = ({
  open,
  onCancel,
  onApply,
  initialSelectedKeys = [],
  selectionMode = 'multiple',
  title,
  fetchWithAuth,
  isTenantAdmin
}) => {
  const { t, currentLanguage } = useLanguage();
  const loc = (key, fb) => {
    const v = t(key);
    return v === key ? fb : v;
  };

  const [keywordInput, setKeywordInput] = useState('');
  /** 已套用至 API 的搜尋字串（僅 Enter／搜尋按鈕時更新） */
  const [searchKeyword, setSearchKeyword] = useState('');
  const [companyId, setCompanyId] = useState(undefined);
  const [companies, setCompanies] = useState([]);
  const [rows, setRows] = useState([]);
  const [rowsTotal, setRowsTotal] = useState(0);
  const [listPage, setListPage] = useState(1);
  const [listPageSize, setListPageSize] = useState(COMMERCE_DEFAULT_PAGE_SIZE);
  const [loading, setLoading] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);
  const selectedCustomerByIdRef = useRef(new Map());
  const pickerWasOpenRef = useRef(false);

  const loadCompanies = useCallback(async () => {
    try {
      const res = await fetchWithAuth('/api/commerce/companies-for-select');
      if (!res.ok) return;
      const data = await res.json();
      setCompanies(Array.isArray(data) ? data : []);
    } catch {
      setCompanies([]);
    }
  }, [fetchWithAuth]);

  const loadCustomers = useCallback(
    async (searchText, cid, page, pageSize) => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (searchText?.trim()) params.set('search', searchText.trim());
        if (isTenantAdmin && cid) params.set('companyId', cid);
        params.set('page', String(page));
        params.set('pageSize', String(pageSize));
        const res = await fetchWithAuth(`/api/commerce/customers?${params}`);
        if (!res.ok) throw new Error(await extractResponseError(res));
        const data = await res.json();
        const raw = Array.isArray(data?.items) ? data.items : [];
        const total = typeof data?.total === 'number' ? data.total : raw.length;
        setRows(raw.map(normalizeCommerceCustomerRowForPicker));
        setRowsTotal(total);
      } catch (err) {
        message.error(err.message);
        setRows([]);
        setRowsTotal(0);
      } finally {
        setLoading(false);
      }
    },
    [fetchWithAuth, isTenantAdmin]
  );

  useLayoutEffect(() => {
    if (!open) {
      pickerWasOpenRef.current = false;
      return;
    }
    if (!pickerWasOpenRef.current) {
      pickerWasOpenRef.current = true;
      setCompanyId(undefined);
      setKeywordInput('');
      setSearchKeyword('');
      setListPage(1);
      setRows([]);
      setRowsTotal(0);
      setHasSearched(false);
      setSelectedKeys(Array.isArray(initialSelectedKeys) ? [...initialSelectedKeys] : []);
      selectedCustomerByIdRef.current = new Map();
    }
  }, [open, initialSelectedKeys]);

  useEffect(() => {
    if (!open || !isTenantAdmin) return;
    loadCompanies();
  }, [open, isTenantAdmin, loadCompanies]);

  useEffect(() => {
    if (!open || !hasSearched) return;
    loadCustomers(searchKeyword, companyId, listPage, listPageSize);
  }, [open, hasSearched, searchKeyword, companyId, listPage, listPageSize, loadCustomers]);

  const rowDisplayName = useCallback(
    (r) => {
      if (r?.labelText) return r.labelText;
      const nl = parseCustomerNameLocalesRoot(r.name_locales);
      return (
        resolveCustomerDisplayName(r.name_1, r.name_2, r.name_3, nl, currentLanguage) ||
        r.customer_no ||
        '—'
      );
    },
    [currentLanguage]
  );

  const columns = useMemo(
    () => [
      { title: t('commerceCustomers.customerNo'), dataIndex: 'customer_no', key: 'customer_no', width: 120 },
      { title: t('commerceCustomers.branchNo'), dataIndex: 'branch_no', key: 'branch_no', width: 90 },
      {
        title: t('commerceCustomers.customerStatus'),
        dataIndex: 'customer_status',
        key: 'customer_status',
        width: 100,
        render: (v) => {
          const s = v || 'Active';
          const color = s === 'Active' ? 'success' : s === 'Suspended' ? 'error' : 'warning';
          const statusLabel =
            s === 'Active'
              ? t('commerceCustomers.statusActive')
              : s === 'Suspended'
                ? t('commerceCustomers.statusSuspended')
                : t('commerceCustomers.statusPending');
          return <Tag color={color}>{statusLabel}</Tag>;
        }
      },
      {
        title: t('commerceCustomers.customerNameCol'),
        key: 'customer_display_name',
        ellipsis: true,
        render: (_, r) => rowDisplayName(r)
      },
      { title: t('commerceCustomers.contactName'), dataIndex: 'contact_name', key: 'contact_name', width: 110 },
      { title: t('commerceCustomers.tel'), dataIndex: 'tel', key: 'tel', width: 120 }
    ],
    [t, rowDisplayName]
  );

  const handleApply = () => {
    const keys = selectionMode === 'single' ? selectedKeys.slice(0, 1) : selectedKeys;
    const map = selectedCustomerByIdRef.current;
    const picked = keys.map((kid) => map.get(String(kid))).filter(Boolean);
    onApply({ customerIds: keys, rows: picked });
  };

  const onTableSelectionChange = (keys) => {
    if (selectionMode === 'single') {
      const k = keys.length ? [keys[keys.length - 1]] : [];
      setSelectedKeys(k);
      const row = k.length ? rows.find((r) => String(r.id) === String(k[0])) : null;
      selectedCustomerByIdRef.current = new Map();
      if (row) selectedCustomerByIdRef.current.set(String(row.id), row);
      return;
    }
    setSelectedKeys(keys);
    const m = selectedCustomerByIdRef.current;
    keys.forEach((id) => {
      const row = rows.find((r) => String(r.id) === String(id));
      if (row) m.set(String(id), row);
    });
    for (const id of [...m.keys()]) {
      if (!keys.map(String).includes(id)) m.delete(id);
    }
  };

  const rowSelection =
    selectionMode === 'single'
      ? {
          type: 'radio',
          selectedRowKeys: selectedKeys.length ? [selectedKeys[0]] : [],
          onChange: onTableSelectionChange
        }
      : {
          selectedRowKeys: selectedKeys,
          onChange: onTableSelectionChange,
          preserveSelectedRowKeys: true
        };

  return (
    <Modal
      title={title}
      open={open}
      onCancel={onCancel}
      width={960}
      destroyOnClose
      footer={
        <Space>
          <Button onClick={onCancel}>{loc('common.cancel', 'Cancel')}</Button>
          <Button type="primary" onClick={handleApply}>
            {loc('common.apply', 'Apply')} ({selectionMode === 'single' ? (selectedKeys[0] ? 1 : 0) : selectedKeys.length})
          </Button>
        </Space>
      }
    >
      <Space wrap style={{ marginBottom: 12 }}>
        {isTenantAdmin && (
          <Select
            allowClear
            placeholder={t('commerceCustomers.filterByCompany')}
            style={{ width: 260 }}
            value={companyId}
            onChange={(value) => {
              setCompanyId(value);
              setListPage(1);
              setHasSearched(true);
            }}
            options={companies.map((c) => ({ label: c.name, value: c.id }))}
          />
        )}
        <Input.Search
          placeholder={t('commerceCustomers.searchPlaceholder')}
          allowClear
          value={keywordInput}
          onChange={(e) => setKeywordInput(e.target.value)}
          onSearch={(v) => {
            setSearchKeyword((v ?? '').trim());
            setListPage(1);
            setHasSearched(true);
          }}
          style={{ width: 320 }}
          prefix={<SearchOutlined />}
          enterButton
        />
        <Button
          icon={<ReloadOutlined />}
          onClick={() => {
            if (hasSearched) loadCustomers(searchKeyword, companyId, listPage, listPageSize);
          }}
          loading={loading}
        >
          {loc('common.refresh', 'Refresh')}
        </Button>
      </Space>
      <Table
        rowKey="id"
        size="small"
        loading={loading}
        dataSource={rows}
        columns={columns}
        locale={{ emptyText: hasSearched ? loc('common.noData', 'No data') : loc('commerceCustomers.searchToLoad', '請輸入關鍵字後搜尋') }}
        pagination={commerceServerTablePagination({
          page: listPage,
          pageSize: listPageSize,
          total: rowsTotal,
          onChange: (p, ps) => {
            setListPage(p);
            setListPageSize(ps);
          }
        })}
        scroll={{ x: 880 }}
        rowSelection={rowSelection}
      />
    </Modal>
  );
};

export default CommerceCustomerTablePickerModal;
